import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
  Collapse,
  Container,
  Avatar as Avator,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { motion, AnimatePresence } from "framer-motion";

const services = [
  {
    name: "Post Construction Cleaning",
    path: "/services/post-construction",
    icon: "🏗️",
  },
  {
    name: "Training & Certifying Cleaners",
    path: "/services/training-certifying",
    icon: "🎓",
  },
  { name: "Hiring Maids", path: "/services/hiring-maids", icon: "👩‍💼" },
  { name: "Fumigation", path: "/services/fumigation", icon: "🐛" },
  {
    name: "Roof Tile Cleaning",
    path: "/services/roof-tile-cleaning",
    icon: "🏠",
  },
  { name: "Deep Cleaning", path: "/services/deep-cleaning", icon: "✨" },
  {
    name: "Laundry Cleaning From Homes",
    path: "/services/laundry-from-homes",
    icon: "👔",
  },
  { name: "Vacuum Cleaning", path: "/services/vacuum-cleaning", icon: "🧹" },
  { name: "Event Cleaning", path: "/services/event-cleaning", icon: "🎉" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleServicesClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleServicesClose = () => {
    setAnchorEl(null);
  };

  const handleServiceNavigate = (path) => {
    navigate(path);
    handleServicesClose();
    setMobileOpen(false);
    setServicesOpen(false);
  };

  const drawer = (
    <Box
      sx={{
        width: 320,
        height: "100%",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Elements */}
      <motion.div
        style={{
          position: "absolute",
          top: -50,
          right: -50,
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.1)",
        }}
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.1, 0.3],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      <Box sx={{ position: "relative", zIndex: 2, height: "100%" }}>
        {/* Header Section */}
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid rgba(255,255,255,0.2)",
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h8"
              sx={{
                fontWeight: 700,
                color: "white",
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: isSmallMobile ? "1.2rem" : "1.5rem",
              }}
            >
              Blossom City Cleaning
            </Typography>
            <IconButton onClick={handleDrawerToggle} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Navigation List */}
        <List sx={{ py: 2 }}>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/"
              onClick={handleDrawerToggle}
              sx={{
                color: "white",
                py: 2,
                px: 3,
                "&:hover": {
                  background: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <ListItemText
                primary="Home"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/about"
              onClick={handleDrawerToggle}
              sx={{
                color: "white",
                py: 2,
                px: 3,
                "&:hover": {
                  background: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <ListItemText
                primary="About Us"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              onClick={() => setServicesOpen(!servicesOpen)}
              sx={{
                color: "white",
                py: 2,
                px: 3,
                "&:hover": {
                  background: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <ListItemText
                primary="Services"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
              {servicesOpen ? (
                <ExpandLess sx={{ color: "white" }} />
              ) : (
                <ExpandMore sx={{ color: "white" }} />
              )}
            </ListItemButton>
          </ListItem>

          <Collapse in={servicesOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {services.map((service) => (
                <ListItemButton
                  key={service.path}
                  sx={{
                    pl: 5,
                    py: 1.5,
                    color: "rgba(255,255,255,0.9)",
                    "&:hover": {
                      background: "rgba(255,255,255,0.1)",
                    },
                  }}
                  onClick={() => handleServiceNavigate(service.path)}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="h6">{service.icon}</Typography>
                    <ListItemText
                      primary={service.name}
                      primaryTypographyProps={{ fontSize: "0.9rem" }}
                    />
                  </Box>
                </ListItemButton>
              ))}
            </List>
          </Collapse>

          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/gallery"
              onClick={handleDrawerToggle}
              sx={{
                color: "white",
                py: 2,
                px: 3,
                "&:hover": {
                  background: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <ListItemText
                primary="Gallery"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/pricing"
              onClick={handleDrawerToggle}
              sx={{
                color: "white",
                py: 2,
                px: 3,
                "&:hover": {
                  background: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <ListItemText
                primary="Pricing"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/contact"
              onClick={handleDrawerToggle}
              sx={{
                color: "white",
                py: 2,
                px: 3,
                "&:hover": {
                  background: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <ListItemText
                primary="Contact Us"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          </ListItem>
        </List>

        {/* CTA Button in Drawer */}
        <Box sx={{ p: 3, mt: "auto" }}>
          <Button
            fullWidth
            variant="contained"
            component={Link}
            to="/contact"
            onClick={handleDrawerToggle}
            sx={{
              background: "white",
              color: theme.palette.primary.main,
              fontWeight: 700,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
              boxShadow: "0 4px 20px rgba(255,255,255,0.3)",
              "&:hover": {
                background: "rgba(255,255,255,0.9)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 30px rgba(255,255,255,0.4)",
              },
            }}
          >
            Book Now
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: scrolled
            ? "rgba(255, 255, 255, 0.95)"
            : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.2)" : "none",
          py: scrolled ? 0 : 1,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            sx={{
              justifyContent: "space-between",
              py: scrolled ? 1 : 0,
              minHeight: { xs: 70, md: 80 },
            }}
          >
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link to="/" style={{ textDecoration: "none" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                  // sx={{
                  //   width: 70,
                  //   height: 70,
                  //   borderRadius: 2,
                  //   background: scrolled
                  //     ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                  //     : "rgba(255,255,255,0.2)",
                  //   display: "flex",
                  //   alignItems: "center",
                  //   justifyContent: "center",
                  //   backdropFilter: "blur(10px)",
                  //   border: scrolled
                  //     ? "none"
                  //     : "1px solid rgba(255,255,255,0.3)",
                  // }}
                  >
                    <img
                      src="/images/logo.jpeg"
                      alt="Blossom Cleaning Logo"
                      style={{ width: "90px", borderRadius: "10px" }}
                    />
                  </Box>
                  {/* <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      fontSize: 30,
                      color: scrolled ? "primary.main" : "white",
                      background: scrolled
                        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        : "none",
                      backgroundClip: scrolled ? "text" : "none",
                      WebkitBackgroundClip: scrolled ? "text" : "none",
                      color: scrolled ? "transparent" : "white",
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    Blossom City Cleaning
                  </Typography> */}
                </Box>
              </Link>
            </motion.div>

            {isMobile ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <IconButton
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{
                    color: scrolled ? "primary.main" : "white",
                    background: scrolled
                      ? "rgba(103, 126, 234, 0.1)"
                      : "rgba(255,255,255,0.2)",
                    "&:hover": {
                      background: scrolled
                        ? "rgba(103, 126, 234, 0.2)"
                        : "rgba(255,255,255,0.3)",
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </motion.div>
            ) : (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Button
                  component={Link}
                  to="/"
                  sx={{
                    color: scrolled ? "text.primary" : "white",
                    fontWeight: 600,
                    "&:hover": {
                      background: scrolled
                        ? "rgba(103, 126, 234, 0.1)"
                        : "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Home
                </Button>
                <Button
                  component={Link}
                  to="/about"
                  sx={{
                    color: scrolled ? "text.primary" : "white",
                    fontWeight: 600,
                    "&:hover": {
                      background: scrolled
                        ? "rgba(103, 126, 234, 0.1)"
                        : "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  About Us
                </Button>
                <Button
                  onClick={handleServicesClick}
                  sx={{
                    color: scrolled ? "text.primary" : "white",
                    fontWeight: 600,
                    "&:hover": {
                      background: scrolled
                        ? "rgba(103, 126, 234, 0.1)"
                        : "rgba(255,255,255,0.1)",
                    },
                  }}
                  aria-controls="services-menu"
                  aria-haspopup="true"
                >
                  Services
                </Button>
                <Menu
                  id="services-menu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleServicesClose}
                  MenuListProps={{
                    "aria-labelledby": "services-button",
                  }}
                  PaperProps={{
                    sx: {
                      mt: 2,
                      minWidth: 280,
                      borderRadius: 3,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      overflow: "hidden",
                    },
                  }}
                >
                  {services.map((service) => (
                    <MenuItem
                      key={service.path}
                      onClick={() => handleServiceNavigate(service.path)}
                      sx={{
                        py: 1.5,
                        borderBottom: "1px solid rgba(255,255,255,0.1)",
                        "&:last-child": { borderBottom: "none" },
                        "&:hover": {
                          background: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Typography variant="h6">{service.icon}</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {service.name}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
                <Button
                  component={Link}
                  to="/gallery"
                  sx={{
                    color: scrolled ? "text.primary" : "white",
                    fontWeight: 600,
                    "&:hover": {
                      background: scrolled
                        ? "rgba(103, 126, 234, 0.1)"
                        : "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Gallery
                </Button>
                <Button
                  component={Link}
                  to="/pricing"
                  sx={{
                    color: scrolled ? "text.primary" : "white",
                    fontWeight: 600,
                    "&:hover": {
                      background: scrolled
                        ? "rgba(103, 126, 234, 0.1)"
                        : "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Pricing
                </Button>
                <Button
                  component={Link}
                  to="/contact"
                  sx={{
                    color: scrolled ? "text.primary" : "white",
                    fontWeight: 600,
                    "&:hover": {
                      background: scrolled
                        ? "rgba(103, 126, 234, 0.1)"
                        : "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Contact Us
                </Button>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="contained"
                    component={Link}
                    to="/contact"
                    sx={{
                      ml: 2,
                      background: scrolled
                        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        : "white",
                      color: scrolled ? "white" : "primary.main",
                      fontWeight: 700,
                      px: 3,
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: scrolled
                        ? "0 4px 20px rgba(103, 126, 234, 0.3)"
                        : "0 4px 20px rgba(255,255,255,0.3)",
                      "&:hover": {
                        background: scrolled
                          ? "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)"
                          : "rgba(255,255,255,0.9)",
                        boxShadow: scrolled
                          ? "0 8px 30px rgba(103, 126, 234, 0.4)"
                          : "0 8px 30px rgba(255,255,255,0.4)",
                      },
                    }}
                  >
                    Book Now
                  </Button>
                </motion.div>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Spacer for fixed header */}
      <Box sx={{ height: { xs: 70, md: 80 } }} />

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          "& .MuiDrawer-paper": {
            boxShadow: "-20px 0 60px rgba(0,0,0,0.3)",
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
